import { ApiError, adminPath, type AdminClient, type ApiResponse } from '../core/index.js'
import type { ActivatedClient, AdminClientSummary, ClientPurge, DemoHistoryInput, DemoHistoryJob, ImportResult, PublicJwk, RegistrationContact, RegistrationInput } from './types.js'

/**
 * A file the API refused outright because rows failed validation - nothing
 * from it was imported. `rows` are 1-indexed positions in the file (0 means
 * the file as a whole could not be read).
 */
export class ImportValidationError extends Error {
  constructor(public readonly rows: { row: number; error: string }[]) {
    super('The file has rows that are not valid, so nothing was imported')
    this.name = 'ImportValidationError'
  }
}

/** The clients module's admin calls: the client list, onboarding a registration, its contact and scopes, the bulk imports and the demo delete. */
export interface ClientsAdminApi {
  listClients(): Promise<AdminClientSummary[]>
  createRegistration(input: RegistrationInput): Promise<{ registrationId: string; status: string }>
  registerKey(registrationId: string, publicJwk: PublicJwk): Promise<{ challenge: string; expiresAt: string }>
  submitProof(registrationId: string, proof: string): Promise<ActivatedClient>
  /** Removes a registration that never activated (no clientId); the API refuses one that has. Frees its key id. */
  deleteRegistration(registrationId: string): Promise<void>
  updateContact(registrationId: string, contact: RegistrationContact): Promise<RegistrationContact>
  /** Replaces the client's scopes with exactly this list. Takes effect on the next token the client is issued. */
  updateScopes(registrationId: string, scopes: string[]): Promise<{ requestedScopes: string[] }>
  /** Whether the client's own operators and facility administrators may use View as…. The admin's Open as… is never governed by it. */
  updateViewAs(registrationId: string, staffViewAs: boolean): Promise<{ staffViewAs: boolean }>
  /** `json` is a JSON array of `{ facilityId, name }`. Upserts, so re-importing a file is safe. */
  importFacilities(clientId: string, json: string): Promise<ImportResult>
  /** `csv` is a roster CSV with a `facilityId` column per row. Upserts by `workerId`. */
  importWorkers(clientId: string, csv: string): Promise<ImportResult>
  /** `json` is a JSON array of full facility configs, each naming its `facilityId`. */
  importFacilityConfigs(clientId: string, json: string): Promise<ImportResult>
  /** `csv` has the columns date, unitId, period, startTime, endTime, and optionally role, capacity and requiredCredentials. */
  importShifts(clientId: string, facilityId: string, csv: string): Promise<ImportResult>
  /** Permanently deletes a DEMO client and everything under it; the API refuses any other client. Starts the delete (or answers the one under way) - the record, to follow with `clientPurge`. */
  deleteDemoClient(clientId: string): Promise<ClientPurge>
  clientPurge(clientId: string): Promise<ClientPurge>
  /** Starts writing a demo client's months of history in the background (demo history plan H1); the record, to follow with `demoHistory`. */
  startDemoHistory(clientId: string, input: DemoHistoryInput): Promise<DemoHistoryJob>
  demoHistory(clientId: string): Promise<DemoHistoryJob>
  /** Adds up a demo client's Insights months now (executive insights plan section 7): this month and last for the facilities named. */
  recomputeInsights(clientId: string, input: { facilityIds?: string[] }): Promise<{ facilities: number; periods: string[]; rollups: number }>
}

export function createClientsAdminApi(client: AdminClient): ClientsAdminApi {
  // The bulk-import endpoints take the file's text as the raw body, and reject an invalid file with the offending rows
  // under `data.errors` rather than the usual single message.
  async function upload(path: string, contentType: string, content: string): Promise<ImportResult> {
    const response = await client.request(path, { method: 'POST', headers: { 'content-type': contentType }, body: content })
    let body: (ApiResponse<ImportResult> & { data?: { errors?: { row: number; error: string }[] } }) | null = null
    try {
      body = await response.json()
    } catch {
      // Non-JSON body - falls through to the generic failure below.
    }
    if (response.status === 400 && body?.data?.errors?.length) {
      throw new ImportValidationError(body.data.errors as { row: number; error: string }[])
    }
    if (!response.ok || !body?.success || !body.data) {
      throw new ApiError(body?.error ?? `Import failed (${response.status})`, response.status)
    }
    return body.data as ImportResult
  }

  return {
    listClients: () => client.call('GET', '/admin/clients'),
    createRegistration: (input) => client.call('POST', '/admin/m2m/registrations', input),
    registerKey: (registrationId, publicJwk) => client.call('POST', `${adminPath.registration(registrationId)}/keys`, { jwks: { keys: [publicJwk] } }),
    submitProof: (registrationId, proof) => client.call('POST', `${adminPath.registration(registrationId)}/proof`, { proof }),
    deleteRegistration: (registrationId) => client.remove(adminPath.registration(registrationId)),
    updateContact: (registrationId, contact) => client.call('PATCH', `${adminPath.registration(registrationId)}/contact`, contact),
    updateScopes: (registrationId, scopes) => client.call('PATCH', `${adminPath.registration(registrationId)}/scopes`, { scopes }),
    updateViewAs: (registrationId, staffViewAs) => client.call('PATCH', `${adminPath.registration(registrationId)}/view-as`, { staffViewAs }),
    importFacilities: (clientId, json) => upload(`${adminPath.client(clientId)}/import/facilities`, 'application/json', json),
    importWorkers: (clientId, csv) => upload(`${adminPath.client(clientId)}/import/workers`, 'text/csv', csv),
    importFacilityConfigs: (clientId, json) => upload(`${adminPath.client(clientId)}/import/facility-configs`, 'application/json', json),
    importShifts: (clientId, facilityId, csv) => upload(`${adminPath.facility(clientId, facilityId)}/import/shifts`, 'text/csv', csv),
    deleteDemoClient: (clientId) => client.call('DELETE', adminPath.client(clientId)),
    clientPurge: (clientId) => client.call('GET', `${adminPath.client(clientId)}/purge`),
    startDemoHistory: (clientId, input) => client.call('POST', `${adminPath.client(clientId)}/demo-history`, input),
    demoHistory: (clientId) => client.call('GET', `${adminPath.client(clientId)}/demo-history`),
    recomputeInsights: (clientId, input) => client.call('POST', `${adminPath.client(clientId)}/insights/recompute`, input),
  }
}
